"use client";

/**
 * Integration-status matrix for the /integrations hub.
 *
 * Renders every entry of INTEGRATION_MATRIX (src/lib/marketing-integrations.ts)
 * as a card: name + status chip, then the four buyer questions — supported
 * actions, approvals & visibility, plan, limitations. Planned entries render
 * compact (roadmap one-liner only), so nothing planned can visually pass for
 * shipped. Copy comes from the `integrationsMatrix` namespace (EN + NL).
 */

import Link from "next/link";
import { INTEGRATION_MATRIX, type IntegrationStatus } from "@/lib/marketing-integrations";
import { Section, SectionHeader } from "@/components/marketing/marketing-kit";
import { Reveal } from "@/components/reveal";
import { useT } from "@/i18n";

const STATUS_STYLE: Record<IntegrationStatus, string> = {
  live: "border-status-ok/30 text-status-ok",
  configured: "border-status-info/30 text-status-info",
  preview: "border-status-warn/30 text-status-warn",
  planned: "border-line-subtle text-fg-muted",
};

export function IntegrationMatrix() {
  const t = useT();
  const k = (s: string) => `integrationsMatrix.${s}`;

  return (
    <Section id="matrix" muted>
      <SectionHeader
        align="left"
        eyebrow={t(k("eyebrow"))}
        title={t(k("title"))}
        description={t(k("description"))}
      />
      <div className="grid gap-4 md:grid-cols-2">
        {INTEGRATION_MATRIX.map((entry, i) => {
          const planned = entry.status === "planned";
          return (
            <Reveal key={entry.id} delay={(i % 4) * 40}>
              <div className="card h-full p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-[15px] font-semibold text-fg-bright leading-snug">
                    {entry.href ? (
                      <Link href={entry.href} className="hover:text-brand-300 transition">
                        {entry.name}
                      </Link>
                    ) : (
                      entry.name
                    )}
                  </h3>
                  <span
                    className={`rounded-full border bg-bg-elev px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLE[entry.status]}`}
                  >
                    {t(k(`statuses.${entry.status}`))}
                  </span>
                </div>
                {planned ? (
                  <p className="mt-3 text-[13px] text-fg-dim leading-relaxed">
                    {t(k(`items.${entry.id}.actions`))}{" "}
                    <span className="text-fg-muted">{t(k(`items.${entry.id}.limits`))}</span>
                  </p>
                ) : (
                  <dl className="mt-3.5 space-y-2.5">
                    {(["actions", "approval", "plan", "limits"] as const).map((field) => (
                      <div key={field}>
                        <dt className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-fg-muted">
                          {t(k(`fields.${field}`))}
                        </dt>
                        <dd className="mt-0.5 text-[13px] text-fg-dim leading-relaxed">
                          {t(k(`items.${entry.id}.${field}`))}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
}
